import { useEffect, useMemo, useState } from "react"
import { Button, FieldType, FieldValue, FormField, FormSeparator, Modal, ModalId, O, option, StaticValue, useEffectAfterMount, useUIActions, useUIStateActions, ValidationModal2 } from "../../../../../features/ui"
import { Budget, Customer, HoardOrder, HoardType, PricesList, SpecialDiscountItem } from "../../../state/types"
import { CreditAccountPaymentTypeId, Data2, DataTypeForm2, Labels, useDataTypesHooks } from "../../../../../features/data-types"
import { useLocation, useNavigate } from "react-router-dom"
import { SellerField } from "../../seller-field/sellerField"
import { PaymentCategory, PaymentTypeField, useAdministrationState, usePaymentTypeLoaders, useCreditPaymentHooks, usePaymentTypeHooks, useTaxHooks } from "../../../../administration"
import { useActions } from "./actions"
import { CustomerField } from "../../customer/customer-field/customerField"
import { OperationItemsTable, OperationItemFormModal, PriceBreakdown, ItemValidation, useOperationHooks, usePricesHooks, usePriceOutlineHooks } from "../../../../operations"
import { TableName } from "../../../../../tables"
import { useManagerHooks } from "../../../../manager"
import { Product } from "../../../../products"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { useSalesHooks } from "../../../hooks/salesHooks"
import { isDefined, round } from "../../../../../utils/typeUtils"
import { useHoardHooks } from "../../../hooks/hoardHooks"
import { HoardPricesList } from "../hoard-prices-list/hoardPricesList"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { useSaleOperationHooks } from "../../../hooks/saleOperationHooks"
import { useCustomerTypeLoaders } from "../../../loaders/customerTypeLoaders"
import { SpecialDiscountItemFormModal } from "../special-discount-item-form-modal/specialDiscountItemFormModal"
import { SpecialDiscountItemsTable } from "../special-discount-items-table/specialDiscountItemsTable"
import styles from './hoardForm.module.css'
import { DeliveryData, DeliveryDataFields } from "../../../../deliveries"

type Props = {
    hoard?: HoardOrder
    budget?: Budget
    customer?: Customer
    keepPrices?: boolean
}

export const HoardForm = (props: Props) => {
    const { hoard, budget, keepPrices } = props
    const createMode = !hoard
    const hoardOrBudget = hoard || budget
    const defaultCustomer = props.customer

    const { currentEmployee } = useAdministrationState()

    const { getSettings } = useSalesHooks()
    const saleSettings = getSettings()
    const generalHoardTermMonths = saleSettings?.generalHoardTermMonths as O<number>
    const hoardIgnoredProductIds = (saleSettings?.hoardIgnoredProductIds || []) as string[]
    const pricesHooks = usePricesHooks()
    const { pathname, state } = useLocation()

    const [customerTypeId, setCustomerTypeId] = useState<O<string>>(hoardOrBudget?.customerTypeId || defaultCustomer?.customerTypeId)

    // Crear desde presupuesto manteniendo precios: cada ítem conserva el precio, ajuste de lista, IVA y descuentos del presupuesto
    const keepBudgetPrices = !hoard && !!budget && !!keepPrices
    // Con precios del presupuesto, los totales usan el ajuste guardado en cada ítem y no la lista actual
    const itemsCustomerTypeId = keepBudgetPrices ? undefined : customerTypeId

    const defaultItemsData = useMemo(() => hoard?.items || (
        (budget?.items || [])
            .filter(item => !hoardIgnoredProductIds.includes(item.productId))
            .map(item => keepBudgetPrices ? {
                ...item,
                id: undefined,
                customerTypeYield: item.customerTypeYield ?? 0
            } : {
                ...item,
                id: undefined,
                price: pricesHooks.product.basePrice(item.productId),
                customerTypeYield: undefined,
                vatTaxYield: undefined,
                discounts: []
            })
    ), [hoard, budget, keepBudgetPrices])

    const initialType = hoard?.type || (state?.type as O<HoardType>) || HoardType.PRODUCT

    const defaultDeliveryData = useMemo(() => ({
        method: hoard?.deliveryData?.method,
        addressId: hoard?.deliveryData?.addressId,
        deliveryDate: hoard?.deliveryData?.deliveryDate
    }), [hoard?.deliveryData])

    const [hoardHasDeliveries, setHoardHasDeliveries] = useState<boolean>(!createMode)
    const [type, setType] = useState<O<HoardType>>(initialType)
    const [customer, setCustomer] = useState<O<Customer>>(defaultCustomer)
    const [itemsData, setItemsData] = useState<Data2[]>(createMode && initialType === HoardType.FINANCIAL ? [] : defaultItemsData)
    const [currentItem, setCurrentItem] = useState<Data2>()
    const [deliveryData, setDeliveryData] = useState<Partial<DeliveryData>>(defaultDeliveryData)
    const [hoardValue, setHoardValue] = useState<O<number>>(hoard?.value)
    const [payedAmount, setPayedAmount] = useState<O<number>>()
    const [paymentTypeId, setPaymentTypeId] = useState<O<string>>(state?.paymentTypeId || hoard?.paymentTypeId)
    const [customerCredit, setCustomerCredit] = useState<O<number>>()
    const [pricesList, setPricesList] = useState<O<PricesList>>(hoard?.pricesList)
    const [pricesListWithoutVat, setPricesListWithoutVat] = useState<O<PricesList>>()
    const [latePayment, setLatePayment] = useState<O<boolean>>(!!hoard?.latePayment)
    const [loading, setLoading] = useState<boolean>(false)
    const [addNotes, setAddNotes] = useState<boolean>(!!hoard?.notes)
    const [notes, setNotes] = useState<O<string>>(hoard?.notes)
    const [applySpecialDiscount, setApplySpecialDiscount] = useState<boolean>(!!(hoard?.specialDiscountItems?.length))
    const [specialDiscountItems, setSpecialDiscountItems] = useState<SpecialDiscountItem[]>(hoard?.specialDiscountItems || [])
    const [currentSpecialDiscountItem, setCurrentSpecialDiscountItem] = useState<O<SpecialDiscountItem>>()
    const [pendingCustomerTypeId, setPendingCustomerTypeId] = useState<O<string>>()
    const [budgetDiscountsInitialized, setBudgetDiscountsInitialized] = useState<boolean>(!!hoard)
    const [budgetDiscountsCleared, setBudgetDiscountsCleared] = useState<boolean>(false)

    const { currentBranchId } = useManagerHooks()
    const { getHoardValue } = useHoardHooks()
    const { hasDeliveries } = useSaleOperationHooks()
    const { validateItems, getNegativeStockWarnings } = useOperationHooks()
    const { getVat21Tax } = useTaxHooks()
    const { getPriceOutlineLive, getPriceOutlineLiveFromTotal, getPriceOutlineLiveFromHoardValue, getItemPriceOutlineLive } = usePriceOutlineHooks()
    const { getCustomerDebtData, getOverdueDebtData } = useCreditPaymentHooks()
    const { formatPrice } = useDataTypesHooks()
    const { getPaymentTypeYield, isCreditAccount } = usePaymentTypeHooks()
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const customerLoaders = useCustomerLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()

    const { submitHoard } = useActions()
    const { openModal, closeModal } = useUIActions()
    const { setDisableBranchSelector } = useUIStateActions()

    // Los acopios anteriores al cambio de facturación no tienen vatTaxYield: su valor no discrimina IVA
    const vatTaxYield = useMemo(() => createMode ? getVat21Tax().yield : hoard?.vatTaxYield || 0, [hoard?.vatTaxYield])

    useEffect(() => {
        setDisableBranchSelector(true)
        if (!createMode && type === HoardType.FINANCIAL) {
            const defaultPayedAmount = getPriceOutlineLiveFromHoardValue(hoardValue, hoard?.paymentTypeYield, vatTaxYield).total
            setPayedAmount(defaultPayedAmount)
        }
        state?.type && setType(state.type)
        state?.payedAmount && setPayedAmount(state.payedAmount)
        state?.customerTypeId && setCustomerTypeId(state.customerTypeId)
        if (createMode && budget && customerTypeId) {
            pricesHooks.product.pricesList(customerTypeId).then(setPricesList)
        }
        return () => {
            setDisableBranchSelector(false)
        }
    }, [])

    useEffect(() => {
        hoard && hasDeliveries(hoard).then(setHoardHasDeliveries)
    }, [hoard])

    useEffectAfterMount(() => {
        pricesHooks.product.pricesList(customerTypeId).then(setPricesList)
    }, [type, customerTypeId])

    useEffectAfterMount(() => {
        const paymentTypeYield = hoard?.paymentTypeId === paymentTypeId ? hoard?.paymentTypeYield : getPaymentTypeYield(paymentTypeId)
        setHoardValue(getHoardValue(payedAmount || 0, paymentTypeYield))
    }, [type, payedAmount, paymentTypeId,])

    useEffect(() => {
        pricesHooks.product.pricesListWithoutVat(pricesList).then(setPricesListWithoutVat)
    }, [pricesList])

    useEffect(() => {
        if (!budgetDiscountsInitialized && budget && pricesList && type === HoardType.FINANCIAL) {
            const seen = new Set<string>()
            const items: SpecialDiscountItem[] = []
            for (const item of budget.items) {
                if (seen.has(item.productId)) continue
                seen.add(item.productId)
                const listPrice = pricesList[item.productId]
                if (listPrice === undefined) continue
                const finalPrice = round(getItemPriceOutlineLive(item, budget.customerTypeId).unitTotal)
                items.push({
                    productId: item.productId,
                    listPrice: round(listPrice),
                    specialDiscountYield: -pricesHooks.adjustment.calculateYield(finalPrice, round(listPrice)),
                    finalPrice: round(finalPrice)
                })
            }
            setSpecialDiscountItems(items)
            setApplySpecialDiscount(items.length > 0)
            setBudgetDiscountsInitialized(true)
        }
    }, [pricesList, type, budget, budgetDiscountsInitialized])

    const priceOutline = useMemo(() => {
        const paymentTypeYield = hoard?.paymentTypeId === paymentTypeId ? hoard?.paymentTypeYield : getPaymentTypeYield(paymentTypeId)
        return type === HoardType.PRODUCT ?
            (createMode ? getPriceOutlineLive(itemsData, itemsCustomerTypeId, paymentTypeYield) : getPriceOutlineLive(itemsData, undefined, paymentTypeYield)) :
            getPriceOutlineLiveFromTotal(payedAmount, paymentTypeYield, vatTaxYield)
    }, [hoard?.paymentTypeId, type, itemsData, payedAmount, itemsCustomerTypeId, paymentTypeId, vatTaxYield])

    const getCustomerCredit = async (creditCustomer: O<Customer>, dbRequest = false): Promise<O<number>> => {
        if (!creditCustomer?.creditAccount || !isCreditAccount(paymentTypeId)) return undefined
        const debtData = await getCustomerDebtData(creditCustomer.id, dbRequest)
        // Al editar solo se descuenta lo que este acopio ya suma a la cuenta corriente (nada si se pagaba con otro medio)
        const thisHoardDebt = createMode ? 0 : debtData.operationsData.find(operationData => operationData.operation.id === hoard?.id)?.debt || 0
        const debtWithoutThisHoard = Math.max(debtData.debt - thisHoardDebt, 0)
        // El crédito a favor se suma al límite: puede llevar el tope por encima del crédito otorgado.
        return round(creditCustomer.creditAccount.creditLimit - debtWithoutThisHoard + (creditCustomer.creditInFavor ?? 0))
    }

    const loadPriceAndDebt = async () => {
        setLoading(true)
        setCustomerCredit(await getCustomerCredit(customer))
        setLoading(false)
    }

    useEffect(() => {
        loadPriceAndDebt()
    }, [customer, paymentTypeId, priceOutline])

    const isProductHoard = useMemo(() => type === HoardType.PRODUCT, [type])
    const defaultDueDate = useMemo(() => {
        const date = new Date()
        date.setMonth(date.getMonth() + (generalHoardTermMonths || 3))
        return date
    }, [])

    const itemMatches = (itemData: Data2, productId: string, lotId?: string) => itemData.productId === productId && itemData.lotId === lotId
    const filterProducts = (product: Product) => !hoardIgnoredProductIds.includes(product.id) &&
        (currentItem?.productId === product.id ||
            (pricesHooks.product.isForSale(product.id) && !itemsData.map(itemData => itemData.productId).includes(product.id)))
    const openFormModal = () => openModal(ModalId.OPERATION_ITEM_FORM)

    const customerTypeOptions = useMemo(() => {
        const customerTypes = customerTypeLoaders().findAllCustomerTypes(createMode)
            .filter(customerType => !customerType.archived || customerType.id === customerTypeId)
        return customerTypes.map(customerType => option(customerType.id, customerType.name))
    }, [])

    const paymentTypes = useMemo(() => paymentTypeLoaders().findAllPaymentTypes(createMode)
        .filter(paymentType => paymentType.category !== PaymentCategory.CREDIT_ACCOUNT || customer?.creditAccount)
    , [customer])

    // The effective price list for the special discount modal: restores original prices
    // for products that already have a special discount, so the modal shows the correct list price.
    const effectivePricesList = useMemo<O<PricesList>>(() => {
        if (!pricesList) return undefined
        const result = { ...pricesList }
        specialDiscountItems.forEach(item => {
            result[item.productId] = item.listPrice
        })
        return result
    }, [pricesList, specialDiscountItems])

    const onChangeType = (value?: FieldValue) => {
        const newType = value as O<HoardType>
        setType(newType)
        if (newType === HoardType.FINANCIAL) {
            setItemsData([])
        } else {
            setPayedAmount(0)
        }
    }

    const onChangeCustomer = async (value?: FieldValue) => {
        const { loadCustomer } = customerLoaders()
        const newCustomer = value ? await loadCustomer(value as string) : undefined
        setCustomer(newCustomer)
        createMode && setCustomerTypeId(newCustomer?.customerTypeId)
        setPaymentTypeId(newCustomer?.creditAccount ? CreditAccountPaymentTypeId : undefined)
    }

    const onCreateCustomer = () => {
        const options = {
            state: {
                from: pathname,
                createMode,
                type,
                payedAmount,
                customerTypeId,
                paymentTypeId
            }
        }
        navigate(getRoutePath(RoutePath.CUSTOMERS, RoutePath.CREATE), options)
    }

    const onChangeCustomerType = (value?: FieldValue) => {
        const newCustomerTypeId = value as O<string>
        if (applySpecialDiscount && specialDiscountItems.length > 0 && newCustomerTypeId !== customerTypeId) {
            setPendingCustomerTypeId(newCustomerTypeId)
            openModal(ModalId.SPECIAL_DISCOUNT_PRICE_LIST_CHANGE)
        } else {
            if (createMode && budget && newCustomerTypeId !== customerTypeId && !budgetDiscountsCleared) {
                setBudgetDiscountsInitialized(false)
            }
            setCustomerTypeId(newCustomerTypeId)
        }
    }

    const onApplyDiscountsToNewList = async () => {
        if (!pendingCustomerTypeId) return
        const newPricesList = await pricesHooks.product.pricesList(pendingCustomerTypeId)
        const updatedItems = specialDiscountItems
            .filter(item => !!newPricesList[item.productId])
            .map(item => {
                const newListPrice = newPricesList[item.productId]
                return {
                    ...item,
                    listPrice: newListPrice,
                    finalPrice: pricesHooks.adjustment.apply(newListPrice, -item.specialDiscountYield)
                }
            })
        setSpecialDiscountItems(updatedItems)
        setCustomerTypeId(pendingCustomerTypeId)
        setPendingCustomerTypeId(undefined)
        closeModal(ModalId.SPECIAL_DISCOUNT_PRICE_LIST_CHANGE)
    }

    const onKeepFinalPrices = async () => {
        if (!pendingCustomerTypeId) return
        const newPricesList = await pricesHooks.product.pricesList(pendingCustomerTypeId)
        const updatedItems = specialDiscountItems
            .filter(item => !!newPricesList[item.productId])
            .map(item => {
                const newListPrice = newPricesList[item.productId]
                return {
                    ...item,
                    listPrice: newListPrice,
                    finalPrice: item.finalPrice,
                    specialDiscountYield: -pricesHooks.adjustment.calculateYield(item.finalPrice, newListPrice)
                }
            })
        setSpecialDiscountItems(updatedItems)
        setCustomerTypeId(pendingCustomerTypeId)
        setPendingCustomerTypeId(undefined)
        closeModal(ModalId.SPECIAL_DISCOUNT_PRICE_LIST_CHANGE)
    }

    const onClearSpecialDiscounts = () => {
        setSpecialDiscountItems([])
        setApplySpecialDiscount(false)
        setCustomerTypeId(pendingCustomerTypeId)
        setPendingCustomerTypeId(undefined)
        closeModal(ModalId.SPECIAL_DISCOUNT_PRICE_LIST_CHANGE)
    }

    const onCreateItem = () => {
        setCurrentItem(undefined)
        openFormModal()
    }

    const onEditItem = (productId: string, lotId?: string) => {
        setCurrentItem(itemsData.find(itemData => itemMatches(itemData, productId, lotId)))
        openFormModal()
    }

    const onRemoveItem = (productId: string, lotId?: string) => {
        const newItemsData = itemsData.filter(itemData => !itemMatches(itemData, productId, lotId))
        setItemsData(newItemsData)
    }

    const onChangePayedAmount = (value?: FieldValue) => setPayedAmount(value as O<number>)

    const onChangePaymentType = (value?: FieldValue) => {
        const newPaymentTypeId = value as O<string>
        setLatePayment(false)
        setPaymentTypeId(newPaymentTypeId)
    }

    const onChangeLatePayment = (value?: FieldValue) => setLatePayment(value as O<boolean>)

    const onChangeAddNotes = (value?: FieldValue) => {
        setAddNotes(!!value)
        setNotes(undefined)
    }

    const onChangeNotes = (value?: FieldValue) => setNotes(value as O<string>)

    const onChangeApplySpecialDiscount = (value?: FieldValue) => {
        if (!value && specialDiscountItems.length > 0) {
            setApplySpecialDiscount(false)
            openModal(ModalId.SPECIAL_DISCOUNT_CLEAR_CONFIRM)
        } else {
            setApplySpecialDiscount(!!value)
        }
    }

    const onConfirmClearSpecialDiscounts = () => {
        setSpecialDiscountItems([])
        setBudgetDiscountsCleared(true)
        closeModal(ModalId.SPECIAL_DISCOUNT_CLEAR_CONFIRM)
    }

    const onCancelClearSpecialDiscounts = () => {
        setApplySpecialDiscount(true)
        closeModal(ModalId.SPECIAL_DISCOUNT_CLEAR_CONFIRM)
    }

    const onCreateSpecialDiscountItem = () => {
        setCurrentSpecialDiscountItem(undefined)
        openModal(ModalId.SPECIAL_DISCOUNT_ITEM_FORM)
    }

    const onEditSpecialDiscountItem = (productId: string) => {
        setCurrentSpecialDiscountItem(specialDiscountItems.find(item => item.productId === productId))
        openModal(ModalId.SPECIAL_DISCOUNT_ITEM_FORM)
    }

    const onRemoveSpecialDiscountItem = (productId: string) => {
        setSpecialDiscountItems(specialDiscountItems.filter(item => item.productId !== productId))
    }

    const onSaveSpecialDiscountItem = (newItem: SpecialDiscountItem) => {
        const exists = specialDiscountItems.find(item => item.productId === newItem.productId)
        if (exists) {
            setSpecialDiscountItems(specialDiscountItems.map(item => item.productId === newItem.productId ? newItem : item))
        } else {
            setSpecialDiscountItems([...specialDiscountItems, newItem])
        }
    }

    const filterSpecialDiscountProducts = (product: Product) =>
        currentSpecialDiscountItem?.productId === product.id ||
        (pricesHooks.product.isForSale(product.id) && !specialDiscountItems.map(item => item.productId).includes(product.id))

    const fields: FormField[] = [
        {
            name: 'info-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Información" />
        },
        {
            name: 'type',
            type: FieldType.SELECT,
            label: 'Tipo',
            value: type,
            options: Object.values(HoardType).map(type => option(type, Labels.hoardTypeLabel(type))),
            disabled: !createMode,
            focus: true,
            narrow: true,
            onChange: onChangeType
        },
        SellerField({
            defaultValue: hoard?.sellerId || currentEmployee?.id,
            narrow: true,
            active: createMode,
        }),
        CustomerField({
            value: customer?.id,
            disabled: hoardHasDeliveries,
            showAddress: true,
            narrow: true,
            active: createMode,
            onChange: onChangeCustomer,
            onCreateNew: createMode ? onCreateCustomer : undefined
        }),
        {
            name: 'customerTypeId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMER_TYPES,
            value: customerTypeId,
            label: 'Lista de Precios',
            options: customerTypeOptions,
            disabled: !createMode,
            narrow: true,
            onChange: onChangeCustomerType
        },
        {
            name: 'dueDate',
            type: FieldType.DATE,
            label: 'Fecha Límite',
            defaultValue: defaultDueDate,
            min: new Date()
        },
        {
            name: 'payment-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Pago" />
        },
        PaymentTypeField({
            value: paymentTypeId,
            paymentTypes,
            onChange: onChangePaymentType
        }),
        {
            name: 'priceBreakdown',
            type: FieldType.PRICE,
            label: 'Precio',
            render: () => <PriceBreakdown data={priceOutline} />
        },
        {
            name: 'addNotes',
            type: FieldType.BOOLEAN,
            label: 'Agregar Notas al Pedido',
            value: addNotes,
            onChange: onChangeAddNotes
        }
    ]

    // Track how many extra fields were inserted before the payment section for index adjustment
    let financialExtraFields = 0

    if (isProductHoard) {
        const itemsField = {
            name: 'hoardItems',
            type: FieldType.TABLE,
            render: () => (
                <OperationItemsTable
                    itemsData={itemsData}
                    createMode={createMode}
                    customerTypeId={itemsCustomerTypeId}
                    onCreateItem={onCreateItem}
                    onEditItem={onEditItem}
                    onRemoveItem={onRemoveItem}
                />
            )
        }
        fields.splice(5, 0, itemsField)
    } else {
        const hoardValueField = {
            name: 'hoardValue',
            type: FieldType.PRICE,
            label: 'Valor Acopiado',
            render: () => <StaticValue type={FieldType.PRICE} value={hoardValue} />
        }
        fields.splice(10, 0, hoardValueField)
        const payedAmountField = {
            name: 'payedAmount',
            type: FieldType.PRICE,
            value: payedAmount,
            label: 'Monto a Pagar',
            onChange: onChangePayedAmount
        }
        fields.splice(9, 0, payedAmountField)

        const pricesListField = {
            name: 'pricesList',
            type: FieldType.TABLE,
            label: 'Lista de Precios',
            loading: !pricesList,
            render: () => <HoardPricesList pricesList={pricesList!} collapsable />
        }
        fields.push(pricesListField)

        const applyDiscountField = {
            name: 'applySpecialDiscount',
            type: FieldType.BOOLEAN,
            label: 'Precios Especiales',
            hint: 'Definir descuentos para productos puntuales. El resto toma el precio de la lista seleccionada.',
            value: applySpecialDiscount,
            disabled: !customerTypeId || !pricesList,
            onChange: onChangeApplySpecialDiscount
        }
        fields.splice(5, 0, applyDiscountField)
        financialExtraFields++

        if (applySpecialDiscount) {
            const specialDiscountTableField = {
                name: 'specialDiscountItems',
                type: FieldType.TABLE,
                label: 'Descuentos Especiales',
                render: () => (
                    <SpecialDiscountItemsTable
                        items={specialDiscountItems}
                        onCreateItem={onCreateSpecialDiscountItem}
                        onEditItem={onEditSpecialDiscountItem}
                        onRemoveItem={onRemoveSpecialDiscountItem}
                    />
                )
            }
            fields.splice(6, 0, specialDiscountTableField)
            financialExtraFields++
        }
    }
    if (addNotes) {
        const notesField: FormField = {
            name: 'notes',
            type: FieldType.LONG_TEXT,
            label: 'Notas',
            value: notes,
            onChange: onChangeNotes
        }
        fields.splice(14 + (isProductHoard ? 0 : financialExtraFields), 0, notesField)
    }
    if (!isCreditAccount(paymentTypeId)) {
        const latePaymentField = {
            name: 'latePayment',
            type: FieldType.BOOLEAN,
            label: 'Pago contra Entrega',
            value: latePayment,
            disabled: hoardHasDeliveries,
            onChange: onChangeLatePayment
        }
        fields.splice((isProductHoard ? 12 : 13) + (isProductHoard ? 0 : financialExtraFields), 0, latePaymentField)
    }
    const deliveryDataFields = [
        ...DeliveryDataFields({
            table: TableName.HOARD_ORDERS,
            defaultData: deliveryData,
            customer,
            addressIdValue: deliveryData.addressId,
            branchId: currentBranchId!,
            onChange: setDeliveryData
        })
    ]
    fields.splice(6, 0, ...deliveryDataFields)

    const defaultPrice = (productId: string) => {
        const productBasePrice = pricesHooks.product.basePrice(productId)
        return createMode ? productBasePrice : pricesListWithoutVat?.[productId] || productBasePrice
    }

    const validate = async (): Promise<string[]> => {
        const validations = createMode && isProductHoard ?
            await validateItems(itemsData, [ItemValidation.NOT_EMPTY, ItemValidation.FOR_SALE, ItemValidation.DEFINED_LOT, ItemValidation.AVAILABLE_STOCK], currentBranchId!) :
            []
        if (validations.length === 0) {
            // Al editar no cuenta la deuda vencida de este mismo acopio
            const overdueDebtData = await getOverdueDebtData(customer!.id, true)
            const overdueDebt = round(overdueDebtData.operationsData
                .filter(operationData => createMode || operationData.operation.id !== hoard?.id)
                .reduce((sum, operationData) => sum + operationData.debt, 0))
            overdueDebt > 0 && validations.push(`El cliente ${customer!.name} tiene deudas vencidas por ${formatPrice(overdueDebt)}`)
        }
        if (validations.length === 0) {
            // Se consulta la base al confirmar para no depender de datos cacheados (ej: operaciones canceladas en otra pestaña)
            const { loadCustomer } = customerLoaders()
            const loadedCustomer = customer ? await loadCustomer(customer.id, true) : undefined
            const updatedCustomerCredit = await getCustomerCredit(loadedCustomer, true)
            setCustomerCredit(updatedCustomerCredit)
            const notEnoughCredit = isDefined(updatedCustomerCredit) && priceOutline.total > updatedCustomerCredit!
            notEnoughCredit && validations.push(`El monto de la venta (${formatPrice(priceOutline.total)}) supera el límite de crédito disponible (${formatPrice(updatedCustomerCredit)}) del cliente ${customer?.name}`)
        }
        return validations
    }

    const onSubmit = async (data: Data2) => {
        // [] quita los descuentos especiales que tuviera el acopio
        const effectiveSpecialDiscountItems = applySpecialDiscount && !isProductHoard ? specialDiscountItems : []
        const finalData = {
            ...data,
            notes: notes,
            internalNotes: hoard?.internalNotes || []
        }
        await submitHoard(
            finalData,
            currentBranchId!,
            itemsData,
            hoardValue,
            deliveryData,
            latePayment,
            notes,
            effectiveSpecialDiscountItems,
            createMode,
            keepBudgetPrices
        )
    }

    const onCancel = () => navigate(-1)

    const onSaveItem = (newData: Data2) => {
        const matches = (data_1: Data2, data_2: Data2) => itemMatches(data_1, data_2.productId! as string, data_2.lotId as O<string>)
        const newItemsData = currentItem ?
            itemsData.map(itemData => matches(itemData, currentItem) ? newData : itemData) :
            itemsData.find(itemData => matches(itemData, newData)) ? itemsData : [...itemsData, newData]
        setItemsData(newItemsData)
    }

    return (
        <>
            <DataTypeForm2
               formId="hoard-form"
               fields={fields}
               createMode={createMode}
               disableSubmit={loading}
               onSubmit={onSubmit}
               onCancel={onCancel}
               validate={validate}
               warn={createMode && isProductHoard ? () => getNegativeStockWarnings(itemsData, currentBranchId!) : undefined}
               warnTitle="Sin stock disponible"
            />
            <OperationItemFormModal
                defaultData={currentItem}
                createMode={createMode}
                customerTypeId={customerTypeId}
                withLots
                defaultPrice={defaultPrice}
                filterProducts={filterProducts}
                onSubmit={onSaveItem}
            />
            {effectivePricesList && (
                <SpecialDiscountItemFormModal
                    defaultData={currentSpecialDiscountItem}
                    pricesList={effectivePricesList}
                    filterProducts={filterSpecialDiscountProducts}
                    onSubmit={onSaveSpecialDiscountItem}
                />
            )}
            <Modal
                modalId={ModalId.SPECIAL_DISCOUNT_PRICE_LIST_CHANGE}
                title="Lista de Precios Cambiada"
                closeable={false}
                actions={[]}
            >
                <>
                    <p className="mb-3">Cambiaste la Lista de Precios, pero ya había productos con descuentos especiales ¿Qué hacemos con esos productos?</p>
                    <hr className={styles.fullWidthDivider} />
                    <div className="d-flex justify-content-center gap-2">
                        <Button label="Mantener Precio Final" onClick={onKeepFinalPrices} />
                        <Button label="Aplicar Descuentos a esta lista" kind="tertiary" onClick={onApplyDiscountsToNewList} />
                    </div>
                    <div className="d-flex justify-content-center mt-2">
                        <Button label="Eliminar Precios Especiales" kind="secondary" onClick={onClearSpecialDiscounts} />
                    </div>
                </>
            </Modal>
            <Modal
                modalId={ModalId.SPECIAL_DISCOUNT_CLEAR_CONFIRM}
                title="Eliminar Precios Especiales"
                submitLabel="Eliminar"
                submitKind="secondary"
                onSubmit={onConfirmClearSpecialDiscounts}
                onCancel={onCancelClearSpecialDiscounts}
            >
                <p>¿Estás seguro? Se van a eliminar todos los precios especiales cargados.</p>
            </Modal>
            <ValidationModal2 title="Validaciones" />
        </>
    )
}
